
import {
  DevFunction, RuntimeError, LoadError, jsonParse, jsonStringify,
  FunctionObject, CLEAR_FLAG, PromiseObject, Environment, LiveJSModule,
  parseString, CSSModule, getString, getPropertyFromObject, ArgTypeError,
  forEachValue, ErrorWrapper, wrapAsync, getAbsolutePath, PathMap,
} from '../../interpreting/ScriptInterpreter.js';
import {scriptParser} from "../../interpreting/parsing/ScriptParser.js";
import {parseRoute} from './src/route_parsing.js';

import {
  CAN_POST_FLAG, ELEVATED_PRIVILEGES_FLAG, NO_TRACE_FLAG
} from "./src/flags.js";



export const upNodeID = "1";

const SCRIPT_ROUTE_REGEX = /^\/.+\.(jsx?|mjs)$/;
const TEXT_FILE_ROUTE_REGEX =
  /^\/.+\.(jsx?|mjs|txt|json|html|xml|svg|md)$/;
const CSS_ROUTE_REGEX = /^\/.+\.css$/;




export const queryRoute = new DevFunction(
  "queryRoute", {
    isAsync: true,
    typeArr: ["string", "boolean?", "any?", "object?"],
  },
  async function(
    {callerNode, execEnv, interpreter},
    [route, isPost = false, postData, options = {}]
  ) {
    // If isPost == true, check if the current environment is allowed to post.
    if (isPost) {
      let canPost = execEnv.getFlag(CAN_POST_FLAG);
      if (!canPost) throw new RuntimeError(
        "Cannot post from here",
        callerNode, execEnv
      );
    }

    // And else change to an environment where the "can-post" flag is set to
    // false.
    else {
      execEnv = new Environment(
        execEnv, undefined, {flags: [[CAN_POST_FLAG, false]]},
      );
    }

    // Parse the route, extracting parameters and qualities from it.
    let isLocked, routeUPNodeID, homeDirID, localPath, dirSegments, fileName,
      fileExt, queryPathSegments;
    try {
    [
      isLocked, routeUPNodeID, homeDirID, localPath, dirSegments, fileName,
      fileExt, queryPathSegments
    ] = parseRoute(route);
    }
    catch(errMsg) {
      throw new RuntimeError(errMsg, callerNode, execEnv);
    }

    // Check against querying a locked route without the isPrivate option being
    // true, or without it being a post request.
    let isPrivate = isPost ||
      getPropertyFromObject(options, "isPrivate", callerNode, execEnv);
    if (isLocked && !isPrivate) throw new RuntimeError(
      "Fetching from a locked route without the 'isPrivate' option set",
      callerNode, execEnv
    );

    // Also make sure to deep-copy the postData if it is an object, and do it
    // via a JSON conversion back and forth such that the object is turned into
    // a plain one.
    if (postData && typeof postData === "object") {
      postData = JSON.parse(jsonStringify(postData));
    }

    // If on the server side, and if upNodeID is that of the UP node that the
    // server is part of, query the database, but not before checking if the
    // privileges are elevated for the given home directory if the route is
    // locked.
    let result;
    if (interpreter.isServerSide && routeUPNodeID === upNodeID) {
      if (isLocked) {
        let curHomeDirID = execEnv.getFlag(ELEVATED_PRIVILEGES_FLAG);
        if (!curHomeDirID || curHomeDirID !== homeDirID) throw new RuntimeError(
          `Requested elevated privileges on Directory ${homeDirID} not granted`,
          callerNode, execEnv
        );
      }
      result = await interpreter.queryDB(
        route, isPost, postData, options,
        homeDirID, localPath, dirSegments, fileName, fileExt, queryPathSegments,
        callerNode, execEnv, interpreter
      );
    }

    // Else query a server at the given UP node.
    else {
      let isPrivate = isPost ||
        getPropertyFromObject(options, "isPrivate", callerNode, execEnv);
      result = await interpreter.queryServer(
        isPrivate, route, isPost, postData, options, routeUPNodeID,
        callerNode, execEnv
      );
    }

    // And finally, return the result.
    return result;
  }
);




export const query = new DevFunction(
  "query", {
    isAsync: true,
    typeArr: ["string", "boolean?", "any?", "object?"],
  },
  function(
    {callerNode, execEnv, interpreter}, [
      extendedRoute, isPost = false, postData, options = {}
    ],
  ) {
    return _query(
      extendedRoute, isPost, postData, options,
      callerNode, execEnv, interpreter
    );
  }
);


export async function _query(
  extendedRoute, isPost = false, postData, options = {},
  callerNode, execEnv, interpreter,
  ancestorModules = [], finalCallbacks = [],
) {
  let {liveModules, queryResults, parsedScripts} = execEnv.globals;
  let isPrivate = isPost ||
    getPropertyFromObject(options, "isPrivate", callerNode, execEnv);
  let result;

  // First get the path and the pathMap, if any, from the caller module, and
  // use it to get the absolute version of extendedRoute (possibly transformed
  // by the pathMap). However, if the resulting route itself points to a
  // pathMap module, starting with "/<nodeID>/<homeDirID>/path_map.js", use an
  // undefined pathMap instead.
  let {modulePath, pathMap} = execEnv.getModuleEnv() ?? {};
  extendedRoute = getAbsolutePath(
    modulePath, extendedRoute, callerNode, execEnv, pathMap
  );
  if (/^\/[^/]+\/[^/]+\/path_map\.js$/.test(extendedRoute)) {
    pathMap = undefined;
  }

  // Then split the input route along each (optional) occurrence of ';',
  // where the first part is then the actual route that is queried, and any
  // and all of the subsequent parts are what we can call "casting paths",
  // which reinterprets/casts the queried result into something else.
  let [route, ...castingSegmentArr] = extendedRoute.split(';');

  // If the route is a script module, then if the given module is not among the
  // so-called "targets" of the pathMap, fetch a potentially new pathMap to be
  // used by the given module. Also change the module's key for the liveModules
  // cache by appending the new pathMap's homePath to it in either case.
  let moduleKey;
  let isScriptRoute = SCRIPT_ROUTE_REGEX.test(route);
  if (!isScriptRoute) {
    moduleKey = route;
  }
  else {
    if (!pathMap || !pathMap.getIsATarget(route, callerNode, execEnv)) {
      pathMap = await fetchPathMap(
        route, callerNode, execEnv, interpreter, ancestorModules,
        finalCallbacks
      );
    }
    moduleKey = route + (pathMap ? ":" + pathMap.homePath : "");
  }

  // Look ahead to see if the first casting segment equals "cache", and if so
  // use the queryResults cache, and increment the casting castingSegmentArr
  // index, i, by one, which is the index that will be used in the for loop
  // below.
  let i = 0;
  let cachedResult, cacheIsNext = castingSegmentArr[i] === "cache";
  if (cacheIsNext) {
    cachedResult = queryResults.get(route)
    if ((cachedResult) ) {
      if (cachedResult instanceof Promise) {
        result = await cachedResult;
      }
      if (result instanceof ErrorWrapper) {
        throw result.val;
      }
      i++;
    }
    else {
      let resultPromise = _query(
        route, isPost, postData, options, callerNode, execEnv, interpreter,
        ancestorModules, finalCallbacks
      ).catch(
        err => ErrorWrapper(err)
      );
      queryResults.set(route, resultPromise);
      result = await resultPromise;
      queryResults.set(route, resultPromise);
      i++;
    }
  }

  // Else query the route itself (but also using the liveModules cache for JS
  // and CSS modules).
  else {
    // If the route is a module that has already been executed, get it from the
    // liveModules cache instead.
    let liveModule = liveModules.get(moduleKey);
    if (liveModule) {
      if (ancestorModules.includes(route)) throw new LoadError(
        `Infinite recursion: Module ${route} imports itself. Ancestor ` +
        "modules when error happened: " + ancestorModules.join(", ") + ".",
        callerNode, execEnv
      );
      if (liveModule instanceof Promise) {
        liveModule = await liveModule;
      }
      if (liveModule instanceof ErrorWrapper) {
        throw liveModule.val;
      }
      result = liveModule;
    }

    // If route is a dev library path, which always comes in the form of a bare
    // module specifier (left over in the build step, if any), try to import
    // the given library.
    else if (route[0] !== "/") {
      let devMod = interpreter.staticDevLibs.get(route);
      if (devMod) {
        liveModule = new LiveJSModule(
          route, Object.entries(devMod), execEnv.globals
        );
        liveModules.set(route, liveModule);
      }
      else if (/^(\.\.?|~)\//.test(route)) {
        throw new LoadError(
          `Excepted absolute route but received: "${route}"`,
          callerNode, execEnv
        );
      }
      else {
        let devLibURL = interpreter.devLibURLs.get(route);
        if (!devLibURL) throw new LoadError(
          `Developer library "${route}" not found`,
          callerNode, execEnv
        );
        try {
          let liveModulePromise = wrapAsync(() =>
            import(devLibURL).then(devMod => {
              return new LiveJSModule(
                route, Object.entries(devMod), execEnv.globals
              );
            })
          );
          liveModules.set(route, liveModulePromise);
          liveModule = await liveModulePromise;
          liveModules.set(route, liveModule);
          if (liveModule instanceof ErrorWrapper) {
            throw liveModule.val;
          }
        } catch (err) {
          throw new LoadError(
            `Developer library "${route}" failed to import ` +
            `from ${devLibURL}`,
            callerNode, execEnv
          );
        }
      }
      result = liveModule;
    }

    // Else if the file has a '.js', '.mjs' or '.jsx' extension, fetch it or
    // get it from the liveModules cache and create and return a LiveJSModule
    // instance.
    else if (isScriptRoute) {
      // First try to get the parsed script from the parsedScripts buffer, or
      // else try to fetch it from the database.
      let [parsedScript, lexArr, strPosArr, script] =
        parsedScripts.get(route) ?? [];
      if (!parsedScript) {
        script = await queryRoute.fun(
          {callerNode, execEnv, interpreter},
          [route, false, undefined, options],
        );
        if (typeof script !== "string") throw new LoadError(
          `No script was found at ${route}`,
          callerNode, execEnv
        );
        [parsedScript, lexArr, strPosArr] = parseString(
          script, callerNode, execEnv, scriptParser
        );
        if (!isPrivate) {
          parsedScripts.set(route, [parsedScript, lexArr, strPosArr, script]);
        }
      }

      // Then execute the module, inside the global environment, and return the
      // resulting liveModule, after also adding it to liveModules.
      let globalEnv = execEnv.getGlobalEnv();
      liveModule = await interpreter.executeModule(
        parsedScript, lexArr, strPosArr, script, route, globalEnv, liveModules,
        pathMap, ancestorModules, finalCallbacks, isPrivate
      );
      result = liveModule;
    }

    // Else if the file a '.css' file, fetch it or get it from the liveModules
    // cache and create and return a CSSModule instance.
    else if (CSS_ROUTE_REGEX.test(route)) {
      let cssModule = liveModules.get(route);
      if (cssModule) {
        if (cssModule instanceof Promise) {
          cssModule = await cssModule;
        }
      }
      else {
        let cssModulePromise = wrapAsync(() =>
          queryRoute.fun(
            {callerNode, execEnv, interpreter},
            [route, false, undefined, options]
          ).then(
            text => new CSSModule(route, text, callerNode, execEnv)
          )
        );
        liveModules.set(route, cssModulePromise);
        cssModule = await cssModulePromise;
        liveModules.set(route, cssModule);
      }
      if (cssModule instanceof ErrorWrapper) {
        throw cssModule.val;
      }
      result = cssModule;
    }

    // Else if the file is a non-JS, non-CSS text file, fetch it and return
    // a string of its content.
    else if (TEXT_FILE_ROUTE_REGEX.test(route)) {
      result = await queryRoute.fun(
        {callerNode, execEnv, interpreter},
        [route, false, undefined, options],
      );
      if (typeof result !== "string") throw new LoadError(
        `No text was found at ${route}`,
        callerNode, execEnv
      );
    }

    // Else simply redirect to queryRoute().
    else {
      result = await queryRoute.fun(
        {callerNode, execEnv, interpreter},
        [route, isPost, postData, options],
      );
    };
  }

  // We now have the result from querying the route itself, but the extended
  // route might also contain extra segments, separated by semicolons, used
  // for "casting" this result into a different form (or extracting data
  // from it, in particular by using ';get/<alias>' segments, which returns
  // a particular export of the given module, or ';call/<alias>[/argument]*'
  // segments which calls the exported function and returns what it returns).
  let len = castingSegmentArr.length;
  for (i = i; i < len; i++) {
    let castingSegment = castingSegmentArr[i];

    // Here we also look ahead to see if the next casting segment equals
    // "cache", similarly to above, except that here we wait to for the next
    // iteration to cache the result if it has not yet been cached.
    if (castingSegmentArr[i + 1] === "cache") {
      let cacheRoute = route + ";" +
        castingSegmentArr.slice(0, i + 2).join(";");
      let cachedResult = queryResults.get(cacheRoute);
      if (cachedResult) {
        if (cachedResult instanceof Promise) {
          result = await cachedResult;
        }
        if (result instanceof ErrorWrapper) {
          throw result.val;
        }
        i++;
        continue;
      }
    }

    // If the casting segment equals cache, simply cache the current result.
    if (castingSegment === "cache") {
      let cacheRoute = route + ";" +
        castingSegmentArr.slice(0, i + 1).join(";");
      queryResults.set(cacheRoute, result);
    }

    // The following casting segment types are for casting between JSON
    // objects (strings) and actual JS objects.
    else if (castingSegment === "parse") {
      result = jsonParse(result, callerNode, execEnv);
    }
    else if (castingSegment === "stringify") {
      result = jsonStringify(result);
    }

    // You can also get the toString() value, and if the result is a JS or CSS
    // module, this casting results in the source code text.
    else if (/^(string|text|\.txt)$/.test(castingSegment)) {
      result = getString(result, callerNode, execEnv, true);
    }

    // You can also cast any string-valued result into a JS or JSX module,
    // or a CSS module.
    else if (/^(exec|\.jsx?|\.mjs)$/.test(castingSegment)) {
      if (result instanceof LiveJSModule) {
        result = getString(result, callerNode, execEnv, true);
      }
      let [parsedScript, lexArr, strPosArr] = parseString(
        result, callerNode, execEnv, scriptParser
      );
      let globalEnv = execEnv.getGlobalEnv();
      let modulePath = route + ";" +
        castingSegmentArr.slice(0, i + 1).join(";");
      let liveModule = await interpreter.executeModule(
        parsedScript, lexArr, strPosArr, result, modulePath, globalEnv,
        liveModules, pathMap, ancestorModules, finalCallbacks,
        isPrivate, false
      );
      result = liveModule;
    }
    else if (/^\.css$/.test(castingSegment)) {
      if (result instanceof CSSModule) {
        return result;
      }
      let modulePath = route + ";" +
        castingSegmentArr.slice(0, i + 1).join(";");
      result = new CSSModule(modulePath, result, callerNode, execEnv);
    }

    // And then we have the ';get' and ';call' casting segments, which work
    // similarly to './get' and './call' routes, except the casting happens on
    // the current machine, and not on the server that is queried. ';get' and
    // ';call' routes are thus often preferred over './get' and './call',
    // as they allow for more efficient use of HTTP caching.
    else if (/^(get|call)\//.test(castingSegment)) {
      let [queryType, alias, ...inputArr] = castingSegment.split("/");
      if (!(result instanceof LiveJSModule)) throw new LoadError(
        "No JS module found at " + route + (
          (i > 0) ? castingSegmentArr.slice(0, i).join(";") : ""
        ),
        callerNode, execEnv
      );
      result = result.get(alias, callerNode, execEnv);
      if (result === undefined) throw new LoadError(
        "No export of the name '" + alias + "' found in " + route + (
          (i > 0) ? castingSegmentArr.slice(0, i).join(";") : ""
        ),
        callerNode, execEnv
      );
      if (queryType === "call") {
        if (!(result instanceof FunctionObject)) throw new LoadError(
          "No function of the name '" + alias + "' exported from " + route + (
            (i > 0) ? castingSegmentArr.slice(0, i).join(";") : ""
          ),
          callerNode, execEnv
        );
        result = interpreter.executeFunction(
          result, inputArr, callerNode, execEnv, undefined, [CLEAR_FLAG]
        );
        if (result instanceof PromiseObject) {
          result = await result.promise;
        }
      }
    }

    // We can also cast a list of all home directory descendants, namely when
    // route is of the form "/<upNodeID>/"<homeDirID>", such that we instead
    // get a list of only the children of a given subdirectory. More precisely,
    // if the casting segment has the form ';dir(/<dirSegment)*>', we check
    // that the previous result is an array of strings, and if so, we treat it
    // as an array of all descendants and extract the children of the given
    // subdirectory pointed to by (/<dirSegment)*.
    else if (/^dir(\/[^/]+)*/.test(castingSegment)) {
      // Throw is result is not an array of strings.
      if (
        !(result instanceof Array) ||
        result.some(val => typeof val !== "string")
      ) {
        throw new LoadError(
          "Result was not an array of file names at " + route + (
            (i > 0) ? castingSegmentArr.slice(0, i).join(";") : ""
          ),
          callerNode, execEnv
        );
      }

      // First transform the result such that each file path is cut off at
      // the first slash that comes after the subdirectory path.
      let subdirectoryPath = castingSegment.substring(4);
      if (subdirectoryPath && subdirectoryPath.at(-1) !== "/") {
        subdirectoryPath += "/";
      }
      let len = subdirectoryPath.length;
      let transformedResult = result.map(val => {
        let indOfNextSlash = val.indexOf("/", len);
        return (indOfNextSlash === -1) ? val.substring(len) :
          val.substring(len, indOfNextSlash);
      });

      // Then filter out all strings where the original string isn't a super-
      // string of subdirectoryPath, as well as all duplicates.
      result = transformedResult.filter((val, ind) => {
        return  (
          (ind === 0 || val !== transformedResult[ind - 1]) && 
          result[ind].substring(0, len) === subdirectoryPath
        );
      });
    }
  
    else throw new LoadError(
      "Unrecognized casting segment: '" + castingSegment + "'",
      callerNode, execEnv
    );
  }

  // And finally, return the result.
  return result;
}


export async function fetchPathMap(
  route, callerNode, execEnv, interpreter,
  ancestorModules = undefined, finalCallbacks = undefined
) {
  // Parse the home path from the route, as well as the rest of it.
  let [ , homePath, tail] = /^(\/[^/]+\/[^/]+)(\/.+)$/.exec(route) ?? [];

  // If the route itself is a path_map.js route, simply return undefined.
  if (!tail || tail.substring(0, 12) === "/path_map.js") {
    return undefined;
  }

  // And else if the PathMap has already been created and cached, return the
  // one from the cache.
  let {pathMaps} = execEnv.globals;
  let pathMap = pathMaps.get(homePath);
  if (pathMap) {
    return pathMap;
  }

  // Else fetch the path_map.js module at that homePath.
  let pathMapModule = await wrapAsync(() =>
    _fetch(
      homePath + "/path_map.js", {},
      callerNode, execEnv, interpreter, ancestorModules, finalCallbacks,
    ).catch(
      err => (err instanceof LoadError) ? {default: {}} : new ErrorWrapper(err)
    )
  );
  if (pathMapModule instanceof ErrorWrapper) throw pathMapModule.val;

  // Finally construct the pathMap, unless another one has already been cached
  // in the meantime, then cache and return it.
  pathMap = pathMaps.get(homePath) ||
    new PathMap(pathMapModule, homePath, callerNode, execEnv);
  pathMaps.set(homePath, pathMap);
  return pathMap;
}






export const fetch = new DevFunction(
  "fetch", {isAsync: true, typeArr: ["string", "object?"]},
  function(
    {callerNode, execEnv, interpreter},
    [extendedRoute, options],
  ) {
    return _fetch(
      extendedRoute, options,
      callerNode, execEnv, interpreter,
    );
  }
);

export async function _fetch(
  extendedRoute, options,
  callerNode, execEnv, interpreter,
  ancestorModules = [], finalCallbacks = [],
) {
  // If returnLog is true, also automatically set isPrivate: true, before
  // calling _query().
  if (
    options && getPropertyFromObject(options, "returnLog", callerNode, execEnv)
  ) {
    let modifiedOptions = {};
    forEachValue(options, callerNode, execEnv, (val, key) => {
      modifiedOptions[key] = val;
    });
    modifiedOptions.isPrivate = true;
    options = modifiedOptions;
  }
  let result = await _query(
    extendedRoute, false, undefined, options,
    callerNode, execEnv, interpreter,
    ancestorModules, finalCallbacks,
  );
  return result;
}




export const fetchPrivate = new DevFunction(
  "fetchPrivate", {isAsync: true, typeArr: ["string", "object?"]},
  function(
    {callerNode, execEnv, interpreter},
    [extendedRoute, options = {}],
  ) {
    return _fetchPrivate(
      extendedRoute, options,
      callerNode, execEnv, interpreter,
    );
  }
);

export async function _fetchPrivate(
  extendedRoute, options,
  callerNode, execEnv, interpreter,
  ancestorModules = [], finalCallbacks = [],
) {
  // Add isPrivate: true to the options object before calling _fetch().
  let modifiedOptions = {};
  forEachValue(options, callerNode, execEnv, (val, key) => {
    modifiedOptions[key] = val;
  });
  modifiedOptions.isPrivate = true;
  options = modifiedOptions;
  let result = await _fetch(
    extendedRoute, options,
    callerNode, execEnv, interpreter,
    ancestorModules, finalCallbacks,
  );
  return result;
}



export const post = new DevFunction(
  "post", {isAsync: true, typeArr: ["string", "any?", "object?"]},
  async function(
    {callerNode, execEnv, interpreter},
    [route, postData, options],
  ) {
    let result = await _query(
      route, true, postData, options,
      callerNode, execEnv, interpreter
    );
    return result;
  }
);





export const encodeURIComponent = new DevFunction(
  "encodeURIComponent", {typeArr: ["string"]}, 
  ({callerNode, execEnv}, [str]) => {
    try {
      return window.encodeURIComponent(str);
    }
    catch (err) {
      throw new ArgTypeError(
        err.message,
        callerNode, execEnv
      );
    }
  }
);

export const decodeURIComponent = new DevFunction(
  "decodeURIComponent", {typeArr: ["string"]}, 
  ({callerNode, execEnv}, [str]) => {
    try {
      return window.decodeURIComponent(str);
    }
    catch (err) {
      throw new ArgTypeError(
        err.message,
        callerNode, execEnv
      );
    }
  }
);

export const encodeURI = new DevFunction(
  "encodeURI", {typeArr: ["string"]}, 
  ({callerNode, execEnv}, [str]) => {
    try {
      return window.encodeURI(str);
    }
    catch (err) {
      throw new ArgTypeError(
        err.message,
        callerNode, execEnv
      );
    }
  }
);

export const decodeURI = new DevFunction(
  "decodeURI", {typeArr: ["string"]}, 
  ({callerNode, execEnv}, [str]) => {
    try {
      return window.decodeURI(str);
    }
    catch (err) {
      throw new ArgTypeError(
        err.message,
        callerNode, execEnv
      );
    }
  }
);




export const clearPermissions = new DevFunction(
  "clearPermissions", {typeArr: ["function"]},
  ({callerNode, execEnv, interpreter}, [callback]) => {
    return interpreter.executeFunction(
      callback, [], callerNode, execEnv, undefined, [CLEAR_FLAG]
    );
  }
);


export const noPost = new DevFunction(
  "noPost", {typeArr: ["function"]},
  ({callerNode, execEnv, interpreter}, [callback]) => {
    return interpreter.executeFunction(
      callback, [], callerNode, execEnv, undefined, [[CAN_POST_FLAG, false]]
    );
  }
);

export const clearPrivileges = new DevFunction(
  "clearPrivileges", {typeArr: ["function"]},
  ({callerNode, execEnv, interpreter}, [callback]) => {
    return interpreter.executeFunction(
      callback, [], callerNode, execEnv, undefined,
      [[ELEVATED_PRIVILEGES_FLAG, false]]
    );
  }
);


// TODO: Implement a setPermissions() function to reduce the permissions, not
// least the "client permissions" object. Do this here and/or in request.js.





// noTrace() suppresses traces from being generated and logged alongside any
// errors thrown while the flag is raised. And if the SMF calls another SMF
// (via './callSMF') any traces made inside the this called SMF should stop
// when reaching the the caller SMF. Use this function whenever the SMF handles
// private data that the client or the caller SMF may not see.
export const noTrace = new DevFunction(
  "noTrace", {typeArr: ["function"]},
  ({callerNode, execEnv, interpreter}, [callback]) => {
    return interpreter.executeFunction(
      callback, [], callerNode, execEnv, undefined, [NO_TRACE_FLAG]
    );
  }
);